import * as ast from '../generated/ast.js';
import { NO_GROUP, SYSTEM_NODES, VALID_BEHAVIORS, VALID_INTENTS, SYSTEM_COMMANDS, } from './constants.js';
import { NodeFields } from './node-fields.js';
export class PifFileSummary {
    constructor(pifFile) {
        // All nodes in the json file saved against their node number
        this.allNodesMap = {};
        // Map of node numbers to their corresponding intRefs
        this.nodeNumbersToIntRefs = {};
        // All system nodes in the json file saved against their node number
        this.allSystemNodesMap = {};
        // All non-system nodes in the json file saved against their node number
        this.allNonSystemNodesMap = {};
        // All nodes in the json file saved against their groupName
        this.groupedNodesMap = {
            [NO_GROUP]: [],
        };
        // All global variables in the json file saved with the nodes that they appear in
        this.globalVariablesMap = {};
        // All commands in the json file saved with the nodes that they appear in
        this.referencedCommandsMap = {};
        // All utterance variables in the json file saved with the nodes that they appear in
        this.utteranceVariablesMap = {};
        // All outgoing edges for each node in the json file saved against the node number
        this.outgoingEdgesMap = {};
        // All incoming edges for each node in the json file saved against the node number
        this.incomingEdgesMap = {};
        // All nodes that have persistent_menu as the rich asset
        this.persistentMenuNodes = [];
        // All nodes that have command set to HandleBotError
        this.handleBotErrorNodes = [];
        // Arrays of all nodes that have intent/behavior of out_of_scope
        this.outOfScopeNodes = [];
        // Saves references to all nodes against platformFlag values
        this.platformFlagsMap = {};
        // All nodes that are unreachable from the start node
        this.unreachableNodes = [];
        // All nodes that have duplicate node numbers
        this.duplicateNodesByNodeNumber = [];
        // All nodes that have duplicate id
        this.duplicateNodesById = [];
        this.serialize = (diagnosticsByNodeNumber) => {
            const serializablePifFileSummary = {
                allNodesMap: Object.fromEntries(Object.entries(this.allNodesMap).map(([key, nodeFields]) => {
                    const nodeNumber = nodeFields.refNode.name;
                    const serialized = nodeFields.serialize();
                    // Add diagnostics if available
                    if (diagnosticsByNodeNumber?.has(nodeNumber)) {
                        const nodeDiagnostics = diagnosticsByNodeNumber.get(nodeNumber);
                        serialized.diagnostics = nodeDiagnostics.map((d) => ({
                            severity: d.severity === 1
                                ? 'error'
                                : d.severity === 2
                                    ? 'warning'
                                    : 'info',
                            message: d.message,
                        }));
                    }
                    return [key, serialized];
                })),
                allSystemNodesMap: Object.fromEntries(Object.entries(this.allSystemNodesMap).map(([key, nodeFields]) => [
                    key,
                    nodeFields.serialize(),
                ])),
                groupedNodesMap: Object.fromEntries(Object.entries(this.groupedNodesMap).map(([key, nodeFieldsArray]) => [
                    key,
                    nodeFieldsArray.map((nodeFields) => nodeFields.serialize()),
                ])),
                outgoingEdgesMap: Object.fromEntries(Object.entries(this.outgoingEdgesMap).map(([key, nodeFieldsArray]) => [
                    key,
                    nodeFieldsArray.map((nodeFields) => nodeFields.serialize()),
                ])),
                incomingEdgesMap: Object.fromEntries(Object.entries(this.incomingEdgesMap).map(([key, nodeFieldsArray]) => [
                    key,
                    nodeFieldsArray.map((nodeFields) => nodeFields.serialize()),
                ])),
            };
            return serializablePifFileSummary;
        };
        this.getAllPlatformFlags = () => {
            return Object.keys(this.platformFlagsMap);
        };
        this.hasNodeGroups = () => {
            return Object.keys(this.groupedNodesMap).length > 1;
        };
        this.getAllGroupNames = () => {
            const groupNames = Object.keys(this.groupedNodesMap);
            return groupNames.filter((groupName) => groupName !== NO_GROUP);
        };
        this.getNodesWithoutGroup = () => {
            return this.groupedNodesMap[NO_GROUP];
        };
        this.getNodesForGroup = (groupName) => {
            return this.groupedNodesMap[groupName] || [];
        };
        this.getStartNodeForGroup = (groupName) => {
            const nodes = this.groupedNodesMap[groupName] || [];
            return nodes[0];
        };
        this.getNodeByNodeNumber = (nodeNumber) => {
            return this.allNodesMap[nodeNumber];
        };
        this.getIntRefForNodeNumber = (nodeNumber) => {
            if (nodeNumber) {
                return this.nodeNumbersToIntRefs[nodeNumber];
            }
            return '';
        };
        this.refPifFile = pifFile;
        this.firstNode = new NodeFields(pifFile.nodes[0]);
        const systemNodeNumbers = Object.values(SYSTEM_NODES);
        const allNodesByIdMap = {};
        pifFile.nodes.forEach((node) => {
            const nodeFields = new NodeFields(node);
            // Save mapping of this node number to it's intRef
            this.nodeNumbersToIntRefs[nodeFields.nodeNumber] = nodeFields.intRef;
            const { idField, behaviorsField, commandField, groupNameField, groupOrderField, intentField, platformFlagField, richAssetField, globalVariables, utteranceVariable, } = nodeFields;
            // If another node with same node number has already been
            // encountered, then add it to the duplicate nodes array
            // Otherwise add it to the allNodesMap
            if (this.allNodesMap[node.name]) {
                this.duplicateNodesByNodeNumber.push(nodeFields);
            }
            else {
                this.allNodesMap[node.name] = nodeFields;
            }
            const id = idField?.id?.value;
            if (id && allNodesByIdMap[id]) {
                this.duplicateNodesById.push(nodeFields);
            }
            else if (id) {
                allNodesByIdMap[id] = true;
            }
            const nodeNumber = Number(node.name);
            if (nodeNumber > 0 ||
                (!isNaN(nodeNumber) &&
                    nodeNumber < 0 &&
                    systemNodeNumbers.indexOf(node.name) === -1)) {
                // If the node number is positive,
                // or if the node number is negative but it is not
                // a recognized system node number, put it in the
                // non-system nodes map.
                this.allNonSystemNodesMap[node.name] = nodeFields;
            }
            else {
                // Otherwise put it in the system nodes map
                this.allSystemNodesMap[node.name] = nodeFields;
            }
            if (node.name === '1') {
                this.startNode = nodeFields;
            }
            if (groupNameField &&
                groupNameField.groupName.$type !== 'EmptyField' &&
                groupOrderField &&
                groupOrderField.groupOrder.$type !== 'EmptyField' &&
                isNaN(Number(groupOrderField.groupOrder.value)) === false) {
                // If both a groupName and groupOrder is defined for this node
                // then add it to the map against that groupName.
                const groupName = groupNameField.groupName.value;
                if (!this.groupedNodesMap[groupName]) {
                    this.groupedNodesMap[groupName] = [];
                }
                this.groupedNodesMap[groupName].push(nodeFields);
            }
            else {
                // Otherwise save it in the no-group entry in the map
                this.groupedNodesMap[NO_GROUP].push(nodeFields);
            }
            if (intentField?.intent?.value === VALID_INTENTS.OUT_OF_SCOPE) {
                this.outOfScopeNodes.push(nodeFields);
            }
            else {
                behaviorsField?.behaviors?.forEach((behavior) => {
                    if (behavior.value === VALID_BEHAVIORS.OUT_OF_SCOPE) {
                        this.outOfScopeNodes.push(nodeFields);
                    }
                });
            }
            if (platformFlagField &&
                ast.isStringField(platformFlagField.platformFlag)) {
                const platformFlag = platformFlagField.platformFlag.value;
                if (!this.platformFlagsMap[platformFlag]) {
                    this.platformFlagsMap[platformFlag] = [];
                }
                this.platformFlagsMap[platformFlag].push(nodeFields);
            }
            // Check if the richAsset type is set to persistent menu
            if (ast.isRichAssetPersistentMenu(richAssetField?.content)) {
                this.persistentMenuNodes.push(nodeFields);
            }
            if (commandField && ast.isStringField(commandField.command)) {
                const command = commandField.command.value;
                // Check if the command is set to HandleBotError
                if (command === SYSTEM_COMMANDS.HANDLE_BOT_ERROR) {
                    this.handleBotErrorNodes.push(nodeFields);
                }
                if (!this.referencedCommandsMap[command]) {
                    this.referencedCommandsMap[command] = [];
                }
                this.referencedCommandsMap[command].push(nodeFields);
            }
            globalVariables.forEach((variable) => {
                if (!this.globalVariablesMap[variable]) {
                    this.globalVariablesMap[variable] = [];
                }
                this.globalVariablesMap[variable].push(nodeFields);
            });
            if (utteranceVariable) {
                if (!this.utteranceVariablesMap[utteranceVariable]) {
                    this.utteranceVariablesMap[utteranceVariable] = [];
                }
                this.utteranceVariablesMap[utteranceVariable].push(nodeFields);
            }
        });
        // Iterate through all the nodes in our allNodesMap and get outgoing
        // edges for all the nodes. Also use the outgoing edges to
        // set the incoming edges for the nodes.
        Object.values(this.allNodesMap).forEach((nodeFields) => {
            const outgoingNodeNumbers = nodeFields.childEdgeNodeNumbers;
            const outgoingNodeFields = outgoingNodeNumbers
                .map((outgoingNodeNumber) => this.allNodesMap[outgoingNodeNumber])
                .filter((item) => !!item);
            this.outgoingEdgesMap[nodeFields.nodeNumber] = outgoingNodeFields;
            outgoingNodeFields.forEach((outgoingNodeField) => {
                // Update the incoming edges map for each of these outgoing nodes
                if (!this.incomingEdgesMap[outgoingNodeField.nodeNumber]) {
                    this.incomingEdgesMap[outgoingNodeField.nodeNumber] = [];
                }
                this.incomingEdgesMap[outgoingNodeField.nodeNumber].push(nodeFields);
                // Get the tag from the current node for this outgoing node,
                // and add it as the incoming tag on this outgoing node field
                outgoingNodeField.addIncomingTag(nodeFields.nodeNumber, nodeFields.getOutgoingTagForNode(outgoingNodeField.nodeNumber));
            });
        });
        if (this.startNode) {
            // Find nodes that are unreachabe from the start node
            const nodesAlreadyVisited = {};
            this.visitOutgoingNodes(this.startNode, nodesAlreadyVisited);
            // Iterate through all the non-system nodes and for all nodes that were
            // not visited add them to the array of unreachable nodes.
            const allNodes = Object.values(this.allNonSystemNodesMap);
            allNodes.forEach((nodeFields) => {
                if (!nodesAlreadyVisited[nodeFields.nodeNumber]) {
                    this.unreachableNodes.push(nodeFields);
                }
            });
        }
        else {
            // We did not find a start node so we will set all the nodes
            // as unreachable
            this.unreachableNodes = Object.values(this.allNonSystemNodesMap);
        }
        // If we have groups defined, then sort all the nodes in each
        // group by the incrementing group order
        const allGroupNames = Object.keys(this.groupedNodesMap);
        allGroupNames.forEach((groupName) => {
            if (groupName !== NO_GROUP) {
                this.groupedNodesMap[groupName].sort((a, b) => {
                    const { groupOrderField: groupOrderFieldA } = a;
                    const groupOrderA = Number(groupOrderFieldA?.groupOrder.value);
                    const { groupOrderField: groupOrderFieldB } = b;
                    const groupOrderB = Number(groupOrderFieldB?.groupOrder.value);
                    return groupOrderA - groupOrderB;
                });
            }
        });
    }
    visitOutgoingNodes(nodeFields, nodesAlreadyVisited) {
        if (nodesAlreadyVisited[nodeFields.nodeNumber]) {
            // We already have visited this node, so simply return
            return;
        }
        // Mark this node as visited
        nodesAlreadyVisited[nodeFields.nodeNumber] = true;
        // Get the outgoing edges for this node
        const childEdgeNodes = this.outgoingEdgesMap[nodeFields.nodeNumber];
        childEdgeNodes.forEach((childEdgeNodeFields) => {
            this.visitOutgoingNodes(childEdgeNodeFields, nodesAlreadyVisited);
        });
    }
}
//# sourceMappingURL=pif-file-summary.js.map